/**
 * DotGrid — per-pest 10×10 inspection-point grid for mobile recording.
 *
 * Pure component: derives all display state from props via `dotState`.
 * No internal recording state.
 *
 * §3.2 behaviour:
 *  - Grey  (dot--grey):  pointId > maxExamined — non-navigable
 *  - Green (dot--green): examined + clean for selected pest
 *  - Red   (dot--red):   infested for selected pest (bright)
 *  - All mode adds: is-faint for points infested by OTHER pests (allInfested \ infested)
 *  - is-current: ring highlight + slight scale for currentPoint
 *  - has-photo:  inner border for points with a photo
 */
import React from 'react';
import { dotState } from '../../../scouting/recording';
import './DotGrid.css';

export interface DotGridProps {
  /** Infested point ids for the SELECTED pest. */
  infested: Set<number>;
  /** Union of infested ids across ALL incidence pests (for 'all' mode). */
  allInfested?: Set<number>;
  currentPoint: number;
  maxExamined: number;
  mode: 'solo' | 'all';
  /** Point ids that have a photo attached. */
  photos: Set<number>;
  onPickPoint: (i: number) => void;
}

const TOTAL = 100;

export const DotGrid: React.FC<DotGridProps> = ({
  infested,
  allInfested,
  currentPoint,
  maxExamined,
  mode,
  photos,
  onPickPoint,
}) => {
  const cells: React.ReactNode[] = [];

  for (let i = 0; i < TOTAL; i++) {
    const state = dotState(i, { infested, maxExamined });

    // Build class list
    const classes: string[] = ['dot'];

    if (state === 'grey') {
      classes.push('dot--grey');
    } else if (state === 'red') {
      // Selected pest infested this point — always bright red
      classes.push('dot--red');
    } else {
      // state === 'green'
      if (mode === 'all' && allInfested?.has(i)) {
        // Another pest is infested here — show faint red
        classes.push('is-faint');
      } else {
        classes.push('dot--green');
      }
    }

    if (i === currentPoint) {
      classes.push('is-current');
    }

    if (photos.has(i)) {
      classes.push('has-photo');
    }

    const className = classes.join(' ');
    const isExamined = state !== 'grey';

    cells.push(
      <button
        key={i}
        className={className}
        aria-label={`point ${i + 1}`}
        onClick={isExamined ? () => onPickPoint(i) : undefined}
      />
    );
  }

  return <div className="dot-grid">{cells}</div>;
};

export default DotGrid;
